import React, { useState, useEffect } from 'react';
import axios from 'axios';

const HomeworkPage = () => {
  const [sessionId, setSessionId] = useState(null);
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSection, setSelectedSection] = useState([]);
  const [homeworkData, setHomeworkData] = useState({});
  const [submittedHomework, setSubmittedHomework] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [todayDate, setTodayDate] = useState('');

  // Styles
  const labelStyle = "block font-semibold text-black text-[18px] mb-2";
  const inputStyle = "w-full px-3 py-2 border rounded-lg text-[14px] focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder:text-gray-400 placeholder:text-[14px]";

  // Get sessionId from localStorage
  useEffect(() => {
    const storedSessionId = localStorage.getItem("currentSessionId");
    if (storedSessionId) setSessionId(storedSessionId);

    const today = new Date().toISOString().split('T')[0];
    setTodayDate(today);
  }, []);

  // Fetch classes & today's homework
  useEffect(() => {
    if (!sessionId) return;

    axios.get(` ${process.env.REACT_APP_API_BASE_URL}/api/classes/getClasses?sessionId=${sessionId}`)
      .then(res => setClasses(res.data))
      .catch(() => alert('Error fetching classes'));

    fetchSubmittedHomework(todayDate);
  }, [sessionId, todayDate]);

  // Fetch sections & subjects when class changes
  useEffect(() => {
    if (!selectedClass || !sessionId) return;

    const selectedClassObj = classes.find(c => c._id === selectedClass);
    if (!selectedClassObj) return;

    const className = selectedClassObj.className;

    Promise.all([
      // Sections
     axios.get(
  ` ${process.env.REACT_APP_API_BASE_URL}/api/sections/${encodeURIComponent(className)}/getSectionsByClassName`,
  { params: { sessionId } }
),

      // Subjects
      axios.get(` ${process.env.REACT_APP_API_BASE_URL}/api/subjects/getSubjectsByClassName/${encodeURIComponent(className)}`, {
        params: { sessionId }
      })
    ])
      .then(([sectionsRes, subjectsRes]) => {
        setSections(sectionsRes.data);
        setSubjects(subjectsRes.data);
      })
      .catch(() => alert('Error fetching sections or subjects'));
  }, [selectedClass, sessionId, classes]);

  // Fetch today's homework
  const fetchSubmittedHomework = (date = todayDate) => {
    if (!sessionId) return;

    axios.get(` ${process.env.REACT_APP_API_BASE_URL}/api/homework/getAllHomework?sessionId=${sessionId}`)
      .then(response => {
        const todayHomework = response.data.filter(hw => hw.dateAssigned === date);
        setSubmittedHomework(todayHomework);
      })
      .catch(() => alert('Error fetching homework'));
  };

  const handleHomeworkChange = (subjectName, value) => {
    setHomeworkData({ ...homeworkData, [subjectName]: value });
  };

  const handleSubmit = async () => {
  if (!selectedClass || selectedSection.length === 0 || Object.keys(homeworkData).length === 0) {
    alert("Please fill in all required fields and homework.");
    return;
  }

  const selectedClassObj = classes.find(c => c._id === selectedClass);

  try {
    for (let sectionId of selectedSection) {
      const selectedSectionObj = sections.find(s => s._id === sectionId);

      const homeworkPayload = {
        className: selectedClassObj?.className,
        sectionName: selectedSectionObj?.sectionName,
        homework: homeworkData,
        dateAssigned: todayDate,
        sessionId
      };

      if (editMode) {
        await axios.put(
          `${process.env.REACT_APP_API_BASE_URL}/api/homework/updateHomework/${editId}`,
          homeworkPayload
        );
      } else {
        await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/api/homework/submitHomeworkForDate`,
          homeworkPayload
        );
      }
    }

    alert('Homework submitted successfully!');
    clearForm();
    fetchSubmittedHomework();

  } catch (error) {
    alert('Error submitting or updating homework.');
  }
};

const handleEdit = async (hw) => {
  const selectedClassObj = classes.find(c => c.className === hw.className);

  if (!selectedClassObj) return;

  // Step 1: set class
  setSelectedClass(selectedClassObj._id);

  try {
    const className = selectedClassObj.className;

    // Step 2: fetch sections manually
    const sectionsRes = await axios.get(
      `${process.env.REACT_APP_API_BASE_URL}/api/sections/${encodeURIComponent(className)}/getSectionsByClassName`,
      { params: { sessionId } }
    );

    const fetchedSections = sectionsRes.data;
    setSections(fetchedSections);

    // Step 3: now set section AFTER sections are loaded
    const selectedSectionObj = fetchedSections.find(
      s => s.sectionName === hw.sectionName
    );

    if (selectedSectionObj) {
     setSelectedSection([selectedSectionObj._id]);
    }

    // Step 4: subjects (optional but better for consistency)
    const subjectsRes = await axios.get(
      `${process.env.REACT_APP_API_BASE_URL}/api/subjects/getSubjectsByClassName/${encodeURIComponent(className)}`,
      { params: { sessionId } }
    );
    setSubjects(subjectsRes.data);

  } catch (err) {
    alert("Error loading sections/subjects for edit");
  }

  // Step 5: set homework data
  setHomeworkData(hw.homework);
  setEditMode(true);
  setEditId(hw._id);
};

  const handleDelete = async (id) => {
    if (!sessionId) return;
    if (window.confirm('Are you sure you want to delete this homework?')) {
      try {
        await axios.delete(` ${process.env.REACT_APP_API_BASE_URL}/api/homework/deleteHomework/${id}`, { params: { sessionId } });
        alert('Homework deleted successfully!');
        setSubmittedHomework(submittedHomework.filter(hw => hw._id !== id));
        if (editMode && editId === id) clearForm();
      } catch (error) {
        alert('Error deleting homework.');
      }
    }
  };

const clearForm = () => {
  setSelectedClass('');
  setSelectedSection([]);
  setHomeworkData({});
  setEditMode(false);
  setEditId(null);
  setSubjects([]);
  setSections([]);
};

  const filteredHomework = submittedHomework.filter(hw =>
    hw.className.toLowerCase().includes(searchTerm.toLowerCase()) ||
    hw.sectionName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="container mx-auto p-4">
      {/* HEADER */}
      <div className="flex justify-between items-center mb-6">
        <div className="bg-indigo-100 text-blue-500 text-sm font-semibold px-4 py-2 rounded-lg shadow-sm">
          {new Date(todayDate).toLocaleDateString('en-GB')}
        </div>
      </div>

      {/* CLASS SELECT */}
      <div className="mb-4">
        <label className={labelStyle}>Class Name:</label>
        <select
          value={selectedClass}
          onChange={(e) => setSelectedClass(e.target.value)}
          className={inputStyle}
        >
          <option value="">Select Class</option>
          {classes.map(cls => (
            <option key={cls._id} value={cls._id}>{cls.className}</option>
          ))}
        </select>
      </div>

      {/* SECTION SELECT */}
      {selectedClass && sections.length > 0 && (
  <div className="mb-4">
    <label className="flex items-center gap-2 font-semibold">
  <input
    type="checkbox"
    onChange={(e) => {
      if (e.target.checked) {
        setSelectedSection(sections.map(s => s._id));
      } else {
        setSelectedSection([]);
      }
    }}
  />
  Select All
</label>
    <label className={labelStyle}>Section Name:</label>

    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
      {sections.map(section => (
        <label key={section._id} className="flex items-center gap-2">
          <input
            type="checkbox"
            value={section._id}
            checked={selectedSection.includes(section._id)}
            onChange={(e) => {
              if (e.target.checked) {
                setSelectedSection([...selectedSection, section._id]);
              } else {
                setSelectedSection(selectedSection.filter(id => id !== section._id));
              }
            }}
          />
          {section.sectionName}
        </label>
      ))}
    </div>
  </div>
)}

      {/* SUBJECT HOMEWORK */}
      {selectedClass && selectedSection && subjects.length > 0 && (
        <div className="mb-6">
          <h2 className="text-[18px] font-semibold text-black mb-4">
            Homework for {classes.find(c => c._id === selectedClass)?.className} - {sections.find(s => s._id === selectedSection)?.sectionName}
          </h2>
          {subjects.map(subject => (
            <div key={subject._id} className="mb-4">
              <label className={labelStyle}>{subject.subjectName}:</label>
              <textarea
                value={homeworkData[subject.subjectName] || ''}
                onChange={(e) => handleHomeworkChange(subject.subjectName, e.target.value)}
                className={inputStyle}
                placeholder={`Enter homework for ${subject.subjectName}`}
              />
            </div>
          ))}
        </div>
      )}

      {/* SUBMIT / CANCEL */}
      <div className="flex gap-4 mb-6">
        <button
          onClick={handleSubmit}
          className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          disabled={!selectedClass || !selectedSection || Object.keys(homeworkData).length === 0}
        >
          {editMode ? 'Update Homework' : 'Submit Homework'}
        </button>

        {editMode && (
          <button
            onClick={clearForm}
            className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
          >
            Cancel
          </button>
        )}
      </div>

      {/* TODAY'S HOMEWORK + SEARCH */}
      <div className="mb-4 flex justify-between items-center">
        <h2 className="text-[18px] font-semibold text-black mt-8 mb-4">Today's Homework</h2>
        <input
          type="text"
          placeholder="Search by class or section"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={`${inputStyle} md:w-96`}
        />
      </div>

      {/* TABLE */}
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white text-center border">
          <thead>
            <tr className="bg-gray-200">
              <th className="py-2 px-4 border text-[18px]">Sr No.</th>
              <th className="py-2 px-4 border text-[18px]">Class Name</th>
              <th className="py-2 px-4 border text-[18px]">Section Name</th>
              <th className="py-2 px-4 border text-[18px]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredHomework.length === 0 ? (
              <tr>
                <td colSpan="4" className="py-4 text-gray-500">No homework submitted today.</td>
              </tr>
            ) : (
              filteredHomework.map((hw, index) => (
                <tr key={hw._id} className="hover:bg-gray-100">
                  <td className="py-2 px-4 border">{index + 1}</td>
                  <td className="py-2 px-4 border">{hw.className}</td>
                  <td className="py-2 px-4 border">{hw.sectionName}</td>
                  <td className="py-2 px-4 border">
                    <button
                      onClick={() => handleEdit(hw)}
                      className="bg-yellow-500 text-white px-2 py-1 rounded mr-2 hover:bg-yellow-600"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(hw._id)}
                      className="bg-red-500 text-white px-2 py-1 rounded hover:bg-red-600"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default HomeworkPage;
